import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { FilesField } from '../../../../components/inputs/FilesField';
import { Modal } from '../../../../components/Modal';
import { formatBytes } from '../../../../lib/formatBytes';
import type { FilesApi } from '../casesApi';
import { CASE_FILE_ACCEPT } from '../caseTypes';
import { fileAll } from '../utils/fileAll';
import { samplePdf } from '../utils/sampleCase';

/**
 * Attaches files to a case or a report that already exists — ONE request per file, in order, with
 * the real progress figure on the button.
 *
 * A file that fails does not stop the others: the window stays open naming what did not file, and
 * the reader may try those again. Everything that DID file is already on the record.
 */
export function AttachFilesModal({
  id,
  files,
  maxBytes,
  recordLabel,
  onClose,
  onSaved,
}: {
  id: string;
  files: FilesApi;
  maxBytes: number;
  /** What the files are filed with, in the reader's words — "CMS-001", "this report". */
  recordLabel: string;
  onClose: () => void;
  /**
   * Called with how many filed, whenever at least one did. `done` is true when nothing failed —
   * the caller closes the window then; otherwise it stays open naming what did not file.
   */
  onSaved: (filed: number, done: boolean) => void;
}) {
  const [picked, setPicked] = useState<File[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState<{ name: string; reason: string }[]>([]);

  const oversize = picked.some((file) => file.size > maxBytes);
  const canSave = picked.length > 0 && !oversize && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setProgress(0);
    setFailed([]);

    const failures = await fileAll(files, picked, setProgress);
    const filed = picked.length - failures.length;

    setSaving(false);

    if (failures.length === 0) {
      onSaved(filed, true);
      return;
    }

    // Keep only what failed, so a second Submit retries those and nothing twice.
    setPicked(picked.filter((file) => failures.some((f) => f.name === file.name)));
    setFailed(failures);
    setConfirmed(false);

    if (filed > 0) onSaved(filed, false);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Attach files"
      size="lg"
      id={`${id}-modal`}
      autogenerate={{
        onGenerate: () => setPicked([samplePdf('sample-case-document.pdf')]),
        disabled: saving,
      }}
      confirm={{ id: `${id}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${id}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${id}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel={`Filing ${progress}%`}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {failed.length > 0 ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <ul className="flex flex-col gap-1 text-xs text-red-700">
              {failed.map((f) => (
                <li key={f.name}>
                  {f.name} was not attached: {f.reason}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <FilesField
          id={`${id}-files`}
          label="Files"
          description={`PDF, PNG, JPEG or MP4, up to ${formatBytes(maxBytes)} each. Filed with ${recordLabel}, one at a time.`}
          accept={CASE_FILE_ACCEPT}
          maxBytes={maxBytes}
          files={picked}
          onChange={setPicked}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
